import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { TenantScopeService } from '../../../company/tenant-scope.service';
import { resolveSisterCompanyScope, sisterCompanyFleetAccessMessage } from '../../../shared/utils/sister-company-scope.util';
import { Vehicle } from '../vehicle.model';
import { VehicleService } from '../vehicle.service';
import { FleetOwnershipTransferAudit, FleetOwnershipTransferService } from '../../ownership-transfer/fleet-ownership-transfer.service';
import { FleetOwnershipTransferDialogComponent, OwnershipTransferResource } from '../../ownership-transfer/fleet-ownership-transfer-dialog.component';

@Component({
    selector: 'app-view-vehicle',
    templateUrl: './view-vehicle.component.html',
    styleUrls: ['./view-vehicle.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        BreadcrumbModule,
        ButtonModule,
        ProgressBarModule,
        TagModule,
        TableModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent,
        FleetOwnershipTransferDialogComponent
    ]
})
export class ViewVehicleComponent implements OnInit {
    isLoading = true;
    loadError: string | null = null;
    vehicleId: number;
    vehicle: Vehicle = new Vehicle();
    isReadOnlyResource = false;
    ownershipTransferVisible = false;
    ownershipTransferResources: OwnershipTransferResource[] = [];
    ownershipTransferPairedResource: OwnershipTransferResource | null = null;
    ownershipHistory: FleetOwnershipTransferAudit[] = [];
    currentCompanyId: number | null = null;
    private companyGroupId: number | null = null;
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private vehicleService: VehicleService,
        private sessionStorageService: SessionStorageService,
        private tenantScopeService: TenantScopeService,
        private ownershipTransferService: FleetOwnershipTransferService
    ) { }

    ngOnInit(): void {
        this.currentCompanyId = this.sessionStorageService.getCompany()?.id ?? null;
        this.vehicleId = Number(this.route.snapshot.paramMap.get('id'));
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Vehicles', routerLink: '/fleet/vehicles' },
            { label: 'View' }
        ];

        if (!this.vehicleId) {
            this.isLoading = false;
            return;
        }

        this.initializeScopeAndLoad();
    }

    editVehicle(): void {
        if (this.isReadOnlyResource) {
            return;
        }
        this.router.navigate(['/fleet/vehicle/edit', this.vehicleId], {
            queryParams: this.route.snapshot.queryParams
        });
    }

    trackVehicle(): void {
        this.router.navigate(['/fleet/gps/vehicles', this.vehicleId]);
    }

    backToVehicles(): void {
        this.router.navigate(['/fleet/vehicles'], { queryParams: this.listQueryParams() });
    }

    changeOwnership(): void {
        if (!this.currentCompanyId || !this.vehicleId) return;
        this.ownershipTransferResources = [{ resourceType: 'VEHICLE', resourceId: this.vehicleId, label: this.vehicle.plateNumber || `Vehicle #${this.vehicleId}` }];
        const driver = this.vehicle.currentDriver;
        this.ownershipTransferPairedResource = driver?.id
            ? { resourceType: 'DRIVER', resourceId: driver.id, label: driver.name || `Driver #${driver.id}` }
            : null;
        this.ownershipTransferVisible = true;
    }

    onOwnershipTransferCompleted(targetCompanyId: number): void {
        if (targetCompanyId !== this.currentCompanyId) {
            this.backToVehicles();
            return;
        }
        this.loadVehicle();
    }

    vehicleMetrics(): PageMetric[] {
        return [
            {
                label: 'Registration',
                value: this.vehicle.registrationNumber || '-',
                hint: this.isExpired(this.vehicle.registrationExpiry) ? 'Expired' : 'Active',
                tone: this.isExpired(this.vehicle.registrationExpiry) ? 'danger' : 'success'
            },
            {
                label: 'Type',
                value: this.vehicle.type?.name || '-',
                hint: `${this.vehicle.config?.name || '-'} ${this.vehicle.capacity?.name || ''}`,
                tone: 'info'
            },
            {
                label: 'Make / Model',
                value: this.vehicle.make?.name || '-',
                hint: `${this.vehicle.model?.name || '-'} ${this.vehicle.year?.year || ''}`,
                tone: 'default'
            },
            {
                label: 'Registration City',
                value: this.vehicle.registrationCity?.cityName || '-',
                hint: this.vehicle.registrationCountry?.name || 'No jurisdiction set',
                tone: 'warn'
            }
        ];
    }

    isExpired(value?: number): boolean {
        if (!value) {
            return false;
        }
        return value < Date.now();
    }

    payloadTypes(): string {
        return this.vehicle.payloadTypes?.map(payloadType => payloadType.name).join(', ') || '-';
    }

    tripTypes(): string {
        return this.vehicle.tripTypes?.map(tripType => tripType.name).join(', ') || '-';
    }

    heroImage(): string {
        return this.vehicle.vehicleImages?.[0] || this.vehicle.type?.imageUrl || 'https://storage.googleapis.com/prologix.appspot.com/basic_data/admin_files/TractorUnitBlack.png';
    }

    dateLabel(value?: number | string | Date): string {
        if (!value) {
            return '-';
        }
        return new Date(value).toLocaleDateString('en-GB');
    }

    private initializeScopeAndLoad(): void {
        const applyScope = () => {
            this.companyGroupId = resolveSisterCompanyScope(
                this.tenantScopeService.getAccessSummary(),
                this.currentCompanyId,
                ['fleet.vehicles.view']
            )?.companyGroupId ?? null;
            this.loadVehicle();
        };

        this.tenantScopeService.initializeCurrentAccess().subscribe(() => applyScope());
    }

    private loadVehicle(): void {
        this.loadError = null;
        this.vehicleService.findVehicleByIds(
            'en-US',
            [this.vehicleId],
            this.currentCompanyId ?? undefined,
            this.companyGroupId ?? undefined
        ).subscribe({
            next: response => {
                this.vehicle = response.data[0];
                this.isReadOnlyResource = !!this.vehicle.resourceCompanyId
                    && !!this.currentCompanyId
                    && this.vehicle.resourceCompanyId !== this.currentCompanyId;
                this.loadOwnershipHistory();
                this.isLoading = false;
            },
            error: error => {
                this.loadError = error?.status === 403 && this.route.snapshot.queryParamMap.get('resourceView') === 'sister'
                    ? sisterCompanyFleetAccessMessage('vehicle')
                    : 'Unable to load this vehicle.';
                this.isLoading = false;
            }
        });
    }

    private loadOwnershipHistory(): void {
        if (!this.currentCompanyId || !this.vehicleId) return;
        this.ownershipTransferService.history(this.currentCompanyId, 'VEHICLE', this.vehicleId).subscribe({
            next: history => this.ownershipHistory = history || [],
            error: () => this.ownershipHistory = []
        });
    }

    private listQueryParams(): Record<string, string | number> {
        const queryParams: Record<string, string | number> = {};
        const tab = this.route.snapshot.queryParamMap.get('tab');
        const resourceView = this.route.snapshot.queryParamMap.get('resourceView');
        const memberCompanyId = this.route.snapshot.queryParamMap.get('memberCompanyId');
        if (tab) queryParams['tab'] = tab;
        if (resourceView) queryParams['resourceView'] = resourceView;
        if (memberCompanyId) queryParams['memberCompanyId'] = memberCompanyId;
        return queryParams;
    }
}
